import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import ShareWheelModal from '../components/wheels/ShareWheelModal';
import WheelDisplay from '../components/wheels/WheelDisplay';
import useAuth from '../hooks/useAuth';
import {
  deleteWheel as deleteFirebaseWheel,
  getUserWheels,
  incrementSpinCount as incrementFirebaseSpinCount,
} from '../services/firebaseWheelService';
import {
  deleteStoredWheel,
  getStoredWheels,
  incrementSpinCount as incrementLocalSpinCount,
  StoredWheel,
} from '../services/localStorage';
import { Wheel, WheelEntry } from '../types/wheel';

/**
 * Page for displaying and managing dominant's Wheels for submissives
 */
const MyWheels: React.FC = () => {
  const { currentUser } = useAuth();
  const [wheels, setWheels] = useState<(StoredWheel | Wheel)[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // For tracking which wheels are currently spinning
  const [spinningWheels, setSpinningWheels] = useState<Record<string, boolean>>({});
  // For tracking spin results
  const [spinResults, setSpinResults] = useState<Record<string, WheelEntry | null>>({});
  // For tracking which wheel's share modal is open
  const [shareModalWheel, setShareModalWheel] = useState<(StoredWheel | Wheel) | null>(null);
  // For notification system
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
    visible: boolean;
  }>({
    message: '',
    type: 'success',
    visible: false,
  });

  /**
   * Load wheels from localStorage and Firebase
   */
  const loadWheels = useCallback(async () => {
    setLoading(true);
    setWheels([]); // Clear existing wheels

    // Track all loaded wheels in a temporary array
    let allWheels: (StoredWheel | Wheel)[] = [];

    try {
      // Local wheels loading
      try {
        const localWheels = getStoredWheels().map(wheel => ({
          ...wheel,
          storageType: 'local' as const,
        }));

        // Add local wheels to our collection
        allWheels = [...localWheels];
      } catch (localError) {
        console.error('Error loading local wheels:', localError);
        // Continue to try loading cloud wheels even if local fails
      }

      // Cloud wheels loading if user is signed in
      if (currentUser) {
        try {
          const userWheels = await getUserWheels(currentUser.uid);
          const cloudWheels = userWheels.map(wheel => ({
            ...wheel,
            storageType: 'cloud' as const,
          }));

          // Create a map of existing wheel IDs to prevent duplicates
          const existingWheelIds = new Set(allWheels.map(wheel => wheel.id));

          // Only add cloud wheels that don't already exist in local storage
          const uniqueCloudWheels = cloudWheels.filter(wheel => !existingWheelIds.has(wheel.id));

          // Add unique cloud wheels to our collection
          allWheels = [...allWheels, ...uniqueCloudWheels];
        } catch (cloudError) {
          console.error('Error loading cloud wheels:', cloudError);
          showNotification('Failed to load your cloud-saved wheels', 'error');
        }
      }

      // Set state once with all unique wheels
      setWheels(allWheels);
    } catch (error) {
      console.error('Error in wheel loading process:', error);
      showNotification('Error loading some of your wheels', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load wheels when component mounts or when currentUser changes
  useEffect(() => {
    loadWheels();
  }, [loadWheels]);

  /**
   * Handle wheel deletion
   */
  const handleDeleteWheel = async (wheel: StoredWheel | Wheel) => {
    if (
      window.confirm('Are you sure you want to delete this wheel? This action cannot be undone.')
    ) {
      try {
        if (wheel.storageType === 'local') {
          // Delete from localStorage
          deleteStoredWheel(wheel.id);
        } else {
          // Delete from Firebase
          await deleteFirebaseWheel(wheel.id);
        }
        // Update the state to remove the deleted wheel
        setWheels(prevWheels => prevWheels.filter(w => w.id !== wheel.id));
        showNotification('Wheel deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting wheel:', error);
        showNotification('Failed to delete wheel', 'error');
      }
    }
  };

  /**
   * Quick spin functionality
   */
  const handleQuickSpin = (wheel: StoredWheel | Wheel) => {
    // Prevent spinning if already in progress
    if (spinningWheels[wheel.id]) return;

    // Mark this wheel as spinning
    setSpinningWheels(prev => ({ ...prev, [wheel.id]: true }));

    // Clear previous result
    setSpinResults(prev => ({ ...prev, [wheel.id]: null }));
  };

  /**
   * Handle spin completion
   */
  const handleSpinComplete = async (wheel: StoredWheel | Wheel, entry: WheelEntry) => {
    // Record the result
    setSpinResults(prev => ({ ...prev, [wheel.id]: entry }));

    // Mark wheel as no longer spinning
    setSpinningWheels(prev => ({ ...prev, [wheel.id]: false }));

    try {
      if (wheel.storageType === 'local') {
        // Increment spin count in localStorage
        const updatedWheel = incrementLocalSpinCount(wheel.id);
        if (updatedWheel) {
          // Update the wheel in the list
          setWheels(prevWheels => prevWheels.map(w => (w.id === wheel.id ? updatedWheel : w)));
        }
      } else {
        // Increment spin count in Firebase
        await incrementFirebaseSpinCount(wheel.id);
        // Update the wheel in the list with incremented count
        setWheels(prevWheels =>
          prevWheels.map(w => {
            if (w.id === wheel.id) {
              return { ...w, spinCount: w.spinCount + 1 };
            }
            return w;
          })
        );
      }
    } catch (error) {
      console.error('Error updating spin count:', error);
    }
  };

  /**
   * Show notification
   */
  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({
      message,
      type,
      visible: true,
    });

    // Hide notification after 3 seconds
    setTimeout(() => {
      setNotification(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  /**
   * Share functionality
   */
  const handleShare = (wheel: StoredWheel | Wheel) => {
    // Open the share modal for:
    // 1. Explicitly marked cloud wheels (storageType === 'cloud')
    // 2. Authenticated users (their wheels should use the full modal)
    // 3. Wheels without storageType specified (assume cloud for authenticated users)
    if (wheel.storageType === 'cloud' || currentUser) {
      setShareModalWheel(wheel);
      return;
    }

    // For local wheels only (unauthenticated users), just copy the URL to clipboard
    const wheelUrl = `${window.location.origin}/wheels/${wheel.id}`;

    // Use the Clipboard API to copy the URL
    navigator.clipboard
      .writeText(wheelUrl)
      .then(() => {
        showNotification('Wheel link copied to clipboard!', 'success');
      })
      .catch(err => {
        console.error('Failed to copy URL:', err);
        showNotification('Failed to copy link. Please try again.', 'error');
      });
  };

  /**
   * Close share modal
   */
  const handleCloseShareModal = () => {
    setShareModalWheel(null);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-dark mb-2">My Wheels</h1>
            <p className="text-gray-600">Manage your custom wheels</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Link
              to="/wheels"
              className="flex items-center gap-2 px-4 py-2 border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              Explore
            </Link>
            <Link
              to="/create-wheel"
              className="btn-primary px-4 py-2 bg-primary text-white rounded-lg"
            >
              Create New Wheel
            </Link>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Empty state */}
        {!loading && wheels.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <h3 className="text-xl font-semibold mb-2">You haven&apos;t created any wheels yet</h3>
            <p className="text-gray-600 mb-6">Create your first custom wheel to get started!</p>
            <Link to="/create-wheel" className="px-4 py-2 bg-primary text-white rounded-lg">
              Create New Wheel
            </Link>
          </div>
        )}

        {/* Wheels grid */}
        {!loading && wheels.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wheels.map(wheel => (
              <div
                key={wheel.id}
                className="card p-6 hover:shadow-lg transition-shadow rounded-xl bg-white border border-gray-100"
              >
                {/* Wheel Header */}
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-semibold text-dark">{wheel.name}</h3>
                  <div className="flex items-center">
                    {/* Storage Type Indicator */}
                    <div
                      className="mr-2"
                      title={
                        wheel.storageType === 'local'
                          ? 'Stored locally on this device'
                          : 'Stored in the cloud'
                      }
                    >
                      {wheel.storageType === 'local' ? (
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8l4-2 4 2V6z"></path>
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4 text-blue-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M5.5 16a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 16h-8z"></path>
                        </svg>
                      )}
                    </div>
                    <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full font-medium">
                      {wheel.spinCount} spins
                    </span>
                  </div>
                </div>

                {/* Creation Date */}
                <div className="text-sm text-gray-500 mb-4">
                  Created{' '}
                  {wheel.createdAt
                    ? (() => {
                        // Check if it's a serverTimestamp object (has _methodName property)
                        const createdAtAny = wheel.createdAt as any;
                        if (
                          createdAtAny &&
                          typeof createdAtAny === 'object' &&
                          createdAtAny._methodName === 'serverTimestamp'
                        ) {
                          return 'Recently';
                        }

                        // Check if it's a Date object
                        if (wheel.createdAt instanceof Date) {
                          return wheel.createdAt.toLocaleDateString();
                        }

                        // Check if it's a Firestore Timestamp (has toDate method)
                        if (
                          typeof createdAtAny === 'object' &&
                          typeof createdAtAny.toDate === 'function'
                        ) {
                          return createdAtAny.toDate().toLocaleDateString();
                        }

                        // Check if it's a string (from localStorage)
                        if (typeof wheel.createdAt === 'string') {
                          try {
                            const date = new Date(wheel.createdAt);
                            if (!isNaN(date.getTime())) {
                              return date.toLocaleDateString();
                            }
                          } catch (e) {
                            console.error('Error parsing date string:', e);
                          }
                        }

                        return 'Unknown date';
                      })()
                    : 'N/A'}
                </div>

                {/* Wheel Display */}
                <div className="flex justify-center mb-4 relative">
                  <div className="w-48 h-48 transform hover:scale-105 transition-transform">
                    <WheelDisplay
                      entries={wheel.entries}
                      size={192}
                      colorScheme={wheel.theme}
                      showSpinButton={false}
                      externalSpin={spinningWheels[wheel.id] || false}
                      onSpinComplete={entry => handleSpinComplete(wheel, entry)}
                      hideResultDisplay={true}
                    />
                  </div>
                </div>

                {/* Wheel Info */}
                <div className="flex items-center text-sm text-gray-600 mb-4">
                  <span>
                    <span role="img" aria-label="Entries">
                      🎯
                    </span>{' '}
                    {wheel.entries.length} entries
                  </span>
                </div>

                {/* Call to Action Buttons */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {/* Quick Spin button */}
                  <button
                    onClick={() => handleQuickSpin(wheel)}
                    disabled={spinningWheels[wheel.id]}
                    className="py-2 text-sm text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    {spinningWheels[wheel.id] ? 'Spinning...' : 'Quick Spin'}
                  </button>

                  {/* View Wheel button */}
                  <Link
                    to={`/wheels/${wheel.id}`}
                    className="py-2 text-sm text-center text-white bg-secondary rounded-lg hover:bg-secondary-dark transition-colors"
                  >
                    View Wheel
                  </Link>

                  {/* Edit button */}
                  <Link
                    to={`/edit-wheel/${wheel.id}`}
                    state={{ storageType: wheel.storageType }} // Pass storage type in state
                    className="py-2 text-sm text-center text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Edit
                  </Link>

                  {/* Share button */}
                  <button
                    onClick={() => handleShare(wheel)}
                    className="py-2 text-sm text-white bg-accent rounded-lg hover:bg-accent-dark transition-colors"
                  >
                    Share Wheel
                  </button>

                  {/* Delete button - spans full width */}
                  <button
                    onClick={() => handleDeleteWheel(wheel)}
                    className="col-span-2 py-2 text-sm text-red-600 bg-red-100 rounded-lg hover:bg-red-200 transition-colors mt-2"
                  >
                    Delete Wheel
                  </button>
                </div>

                {/* Spin Result */}
                {spinResults[wheel.id] && (
                  <div className="mt-3 p-3 bg-gray-100 rounded-lg text-center">
                    <h4 className="text-sm font-medium text-gray-700">Result:</h4>
                    <div className="text-lg font-bold text-primary mt-1">
                      {spinResults[wheel.id]?.emoji && (
                        <span className="mr-2">{spinResults[wheel.id]?.emoji}</span>
                      )}
                      {spinResults[wheel.id]?.text}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification */}
      {notification.visible && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg animate-fade-in ${
            notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {notification.message}
        </div>
      )}

      {/* Share Wheel Modal */}
      {shareModalWheel && (
        <ShareWheelModal
          wheel={shareModalWheel}
          isOpen={shareModalWheel !== null}
          onClose={handleCloseShareModal}
        />
      )}
    </Layout>
  );
};

export default MyWheels;
